import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from '../common/FeatherIcon';
import usePins, { MAX_PINS } from './usePins';
import { describePath } from './navRegistry';
import { tabPinLabel, tabPinPath, useOpenTab } from './openTab';
import './pins.css';

/** A heading's text, less the star's own slot. */
export const headingText = (heading, slot) => {
  if (!heading) return '';
  const text = Array.from(heading.childNodes)
    .filter((node) => node !== slot)
    .map((node) => node.textContent || '')
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length <= 80 ? text : '';
};

/**
 * The star beside a page title: pins what the page shows now — its URL with the open tab and
 * filters, less the open tab's slide-panel keys — named "<page> · <tab>" on a tab, else after the
 * page. Always visible (faint until hovered or pinned) and with a tooltip that says what a click
 * does, shown on hover and on keyboard focus and named by aria-describedby.
 */
export default function TitlePinStar({ heading = null, slot = null }) {
  const { t } = useTranslation('nav');
  const { pathname, search } = useLocation();
  const tab = useOpenTab();
  const { isPinned, isFull, toggle } = usePins();
  const [tipAt, setTipAt] = useState(null);
  const ref = useRef(null);
  const tipId = `pins-tip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const path = tabPinPath(pathname, search, tab ? tab.omit : []);
  const pinned = isPinned(path);
  const blocked = isFull && !pinned;
  const page = headingText(heading, slot) || describePath(path).label;
  const name = tab ? tabPinLabel(page, tab.label) : page;
  const tip = blocked
    ? t('nav:pins.full', { max: MAX_PINS })
    : t(pinned ? 'nav:pins.unpinTip' : 'nav:pins.pinTip', { label: name });

  const show = () => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const r = el.getBoundingClientRect();
    setTipAt({ top: Math.round(r.bottom + 8), left: Math.round(Math.min(Math.max(r.left + r.width / 2, 120), window.innerWidth - 120)) });
  };
  const hide = () => setTipAt(null);

  useEffect(() => {
    if (!tipAt) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', onKey);
    };
  }, [tipAt]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className={`nav-pin-star title-pin-star${pinned ? ' is-pinned' : ''}`}
        aria-pressed={pinned}
        aria-label={t(pinned ? 'nav:pins.unpinItem' : 'nav:pins.pinItem', { label: name })}
        aria-disabled={blocked || undefined}
        aria-describedby={tipAt ? tipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (blocked) return;
          toggle({ path, label: name, module: describePath(path).module });
        }}
      >
        <FeatherIcon name="star" size={16} fill={pinned ? 'currentColor' : 'none'} aria-hidden="true" />
      </button>
      {tipAt && typeof document !== 'undefined' && createPortal(
        <span id={tipId} role="tooltip" className="pins-tip" style={{ top: tipAt.top, left: tipAt.left }}>{tip}</span>,
        document.body,
      )}
    </>
  );
}
